import React, { FC, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { mixpanelTrack } from '@providers/Mixpanel';
import * as format from '@format';
import * as ApiModels from '@typings/api-models';
import { MessageType, WorkflowStepEventNameAndData } from '@typings/api-models';
import CheckCircle from '@components/Icons/CheckCircle';
import { useIsMobile, useUser } from '@hooks';
import { SIZES } from '@styles/media';
import { useRouter } from 'next/router';
import AssignCollaboratorsToAttachmentModal from '@modals/AssignCollaboratorsToAttachmentModal';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { FullWorkflowStep } from '@noodleApi/ApiDefinition/workflows';
import { CaretCircleDown } from '@phosphor-icons/react';
import isAttachmentHidden from '@helpers/isAttachmentHidden';
import Spacer from '@components/Spacer';
import isStepCompleted from '@helpers/isStepCompleted';
import CustomLink from '@components/CustomLink';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import RenderMediaAttachments from '@components/Message/RenderMediaAttachments';
import convertLinksToAnchors from '@helpers/convertLinksToAnchors';
import DOMPurify from 'isomorphic-dompurify';
import TextEllipsis from '@/components/TextEllipsis';
import useUserWorkflowContext from '@/hooks/useUserWorkflowContext';
import classes from './UserWorkflowSteps.module.scss';
import UserWorkflowConversation, { OpenConversationButton } from './UserWorkflowConversation';
import Attachment from './Attachment';
import Attachments from './Attachments';

type UserWorkflowStepsProps = {
  workflowSteps: FullWorkflowStep[];
  creator: Pick<ApiModels.Creator, 'slug' | 'personId' | 'id'>;
  onChange: () => Promise<void>;
  userWorkflowId: string;
  customerId: string;
  isWorkflowComplete: boolean;
  assignedToMeOnly: boolean;
};

const UserWorkflowSteps: FC<UserWorkflowStepsProps> = ({
  workflowSteps,
  creator,
  onChange,
  userWorkflowId,
  customerId,
  isWorkflowComplete,
  assignedToMeOnly,
}) => {
  const { collaborators } = useUserWorkflowContext();

  const [user] = useUser();
  const [conversationOpenStepId, setConversationOpenStepId] = useState<string | null>(null);
  const [isAssignCollaboratorModalOpen, setAssignCollaboratorModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<Pick<
    ApiModels.MessageAttachment,
    'id' | 'referenceId' | 'referenceType' | 'canAccess'
  > | null>(null);
  const [fulfilledAttachments, setFulfilledAttachments] = useState<string[]>([]);
  const [conversationSid, setConversationSid] = useState<string | null>(null);
  const { query } = useRouter();
  const areStepAttachmentsFulfilled = (step: FullWorkflowStep): boolean => {
    const attachments = step.stepTaken?.message?.attachments;
    if (!attachments) return false;
    return attachments.every(a => fulfilledAttachments.includes(a.id));
  };

  const isMobile = useIsMobile(SIZES.md);

  const { getData: findOrCreateConversationWithCreator } = useNoodleApi(tsClient.my.findOrCreateConversationWithCreator);

  const lastStep = useMemo(() => {
    const calculateLastStep = (step: FullWorkflowStep): FullWorkflowStep => {
      if (step.nextSteps?.length > 0) {
        return calculateLastStep(step.nextSteps[step.nextSteps.length - 1]);
      }
      return step;
    };
    return calculateLastStep(workflowSteps[workflowSteps.length - 1]);
  }, [workflowSteps]);

  const onAssignCollaborators = async (): Promise<void> => {
    setAssignCollaboratorModalOpen(false);
    await onChange();
  };

  useEffect(() => {
    if (assignedToMeOnly) {
      mixpanelTrack('Workflow - view my to do list', { isCreatorView: false, userWorkflowId });
    }
  }, [assignedToMeOnly, userWorkflowId]);

  useEffect(() => {
    if (creator?.id) {
      findOrCreateConversationWithCreator({
        creatorSlug: creator.slug,
      }).then(d => {
        setConversationSid(d.data?.sid || null);
      });
    }
  }, [creator?.id, creator.slug, findOrCreateConversationWithCreator, setConversationSid]);

  useEffect(() => {
    onChange();
  }, [query, onChange]);

  const renderStep = (step: FullWorkflowStep, hasOnlyOneStep: boolean): ReactElement => {

    // don't render triggers
    if (step.type === 'trigger') {
      return <Fragment key={step.id}>{step.nextSteps.map(childStep => renderStep(childStep, hasOnlyOneStep))}</Fragment>;
    }

    const stepAttachments = step.stepTaken?.message?.attachments?.filter(
      attachment =>
        !isAttachmentHidden({
          attachment,
          creatorPersonId: creator.personId || null,
          customerId,
          personId: user?.id || null,
        }),
    );

    const Component = areStepAttachmentsFulfilled(step) ? 'details' : 'div';

    return (
      <Fragment key={step.id}>
        <Component
          open={(step.id === lastStep.id && isWorkflowComplete) || hasOnlyOneStep}
          className={classes.workflowStep}
          style={{
            backgroundColor: 'var(--color-gray-0)',
            border: '1px solid var(--color-gray-25)',
            borderRadius: 8,
            padding: 16,
          }}
        >
          {areStepAttachmentsFulfilled(step) && (
            <summary className={classes.workflowStep_text_summary}>
              <CheckCircle weight="fill" size={20} color="var(--color-success)" />
              <div>
                <p className="body-sm-bold">
                  {format.stepName({
                    creatorPersonId: creator.personId,
                    customerId,
                    personId: user?.id || null,
                    showTitles: !isStepCompleted(step),
                    step: step as Parameters<typeof format.stepName>[0]['step'], // @todo - fixme
                  })}{' '}
                </p>
                <p className="caption">completed</p>
              </div>
              <CaretCircleDown weight="fill" color="var(--color-primary)" className={classes.caret} />
            </summary>
          )}
          {(!areStepAttachmentsFulfilled(step) && isStepCompleted(step)
            || step.stepTaken?.message?.text
            || ((stepAttachments?.length || 0) > 0
            || (step.stepTaken?.message?.medias?.length || 0) > 0))
            && <div className={classes.workflowStep_text}>
              {!areStepAttachmentsFulfilled(step) && isStepCompleted(step) && (
                <div className={classes.workflowStep_text_title}>
                  <p
                    className="body-sm-bold"
                  >
                    {format.stepNameCustomer({
                      creatorPersonId: creator.personId,
                      customerId,
                      personId: user?.id || null,
                      step: step as FullWorkflowStep & WorkflowStepEventNameAndData,
                    })}{' '}
                    <OpenConversationButton
                      onClick={() => (conversationOpenStepId === step.id ? null : step.id)}
                      isSelected={conversationOpenStepId === step.id}
                    />
                  </p>
                </div>
              )}
              <>
                {step.stepTaken?.message?.text && (
                  <>
                    <TextEllipsis lines={2} buttonClassName={classes.textEllipsisButton}>
                      {step.stepTaken?.message.richText
                        ? (
                          <div
                            className={classes.message__ellipsis}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step.stepTaken?.message.richText.html) }}
                          />
                        )
                        : (
                          <div
                            className={classes.message__ellipsis}
                            style={{ margin: '8px 0 10px 0'}}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(convertLinksToAnchors(step.stepTaken?.message.text)) }}
                          />
                        )}
                    </TextEllipsis>
                    {conversationSid && step.stepTaken?.message.attachments.length > 0 && (
                      <p className={classes.message__helper}>
                        Please complete your assignments below before moving on to the next step. Have questions?{' '}
                        <CustomLink
                          to={getUrl(IDENTIFIERS.CONVERSATION_SID, {
                            conversationSid,
                          })}
                        >
                          Ask here.
                        </CustomLink>
                      </p>
                    )}
                  </>
                )}
                {((stepAttachments?.length || 0) > 0 || (step.stepTaken?.message?.medias?.length || 0) > 0) && <div className={classes.attachments}>
                  <RenderMediaAttachments commentType={MessageType.Chat} medias={step.stepTaken?.message?.medias || []} />
                  {stepAttachments?.map(a => (
                    <Attachment
                      key={a.id}
                      data={a}
                      onChange={onChange}
                      creator={creator}
                      collaborators={collaborators}
                      customerId={customerId}
                      userWorkflowId={userWorkflowId}
                      setAssignCollaboratorModalOpen={setAssignCollaboratorModalOpen}
                      setSelectedAttachment={setSelectedAttachment}
                      canAssignCollaborators={false}
                      userId={user?.id}
                      setFulfilledAttachments={setFulfilledAttachments}
                      fulfilledAttachments={fulfilledAttachments}
                    />
                  ))}
                </div>}
              </>
            </div>}
        </Component>
        {step.nextSteps
          .map(childStep => <>
            {renderStep(childStep, false)}
          </>)}
      </Fragment>
    );
  };

  return (
    <>
      {assignedToMeOnly
        ? (
          <>
            <div>
              <h2 className="body-md-bold">These are tasks that you are assigned to.</h2>
              <Spacer size={4} />
              <p className="caption">Please complete them now or assign them to someone else.</p>
              <Spacer size={16} />
            </div>
            <Attachments
              canAssignCollaborators={false}
              collaborators={collaborators}
              creator={creator}
              customerId={customerId}
              userWorkflowId={userWorkflowId}
              onChange={onChange}
              setAssignCollaboratorModalOpen={setAssignCollaboratorModalOpen}
              setSelectedAttachment={setSelectedAttachment}
              user={user}
              workflowSteps={workflowSteps}
            />
          </>
        )
        : (
          <div className={classes.wrapper}>
            {isAssignCollaboratorModalOpen && selectedAttachment && (
              <AssignCollaboratorsToAttachmentModal
                attachmentType={selectedAttachment.referenceType}
                attachmentReferenceId={selectedAttachment.referenceId}
                collaborators={collaborators}
                onClose={() => setAssignCollaboratorModalOpen(false)}
                onSubmit={onAssignCollaborators}
                userWorkflowId={userWorkflowId}
              />
            )}
            <div className={classes.workflowSteps}>
              {workflowSteps.map(step => {
                if (!isStepCompleted(step)) return null;
                if (step.nextSteps.find(nextStep => nextStep.eventName === ApiModels.WORKFLOW_ACTIONS.GENERATE_ALL_WORKFLOW_PDFS)) return null;
                return (
                  <Fragment key={step.id}>
                    {workflowSteps.filter(
                      s => !s.nextSteps.find(nextStep => nextStep.eventName === ApiModels.WORKFLOW_ACTIONS.GENERATE_ALL_WORKFLOW_PDFS),
                    ).length > 1 && (
                      <div id={step.id} className={classes.stepName}>
                        <h2 className="caption-bold">
                          {format.stepName({
                            creatorPersonId: creator.personId,
                            customerId,
                            personId: user?.id || null,
                            showTitles: !isStepCompleted(step),
                            step: step as FullWorkflowStep & WorkflowStepEventNameAndData,
                          })}
                        </h2>
                        <p className='caption'>Started {step.stepTaken?.updatedAt && format.datetimeLocalTZ.timeSince({ date: step.stepTaken?.updatedAt })} ago</p>
                      </div>
                    )}
                    {renderStep(step, step.nextSteps[0]?.nextSteps.length === 0)}
                    <Spacer size={12} />
                  </Fragment>
                );
              })}
            </div>
            <UserWorkflowConversation
              conversationOpenStepId={conversationOpenStepId}
              isMobile={isMobile}
              onClose={() => setConversationOpenStepId(null)}
            />
          </div>
        )}
    </>
  );
};

export default UserWorkflowSteps;
